// Where the emailed reset link lands (/reset-password?token=…).
//
// The Worker does the part that matters here: alongside the new password hash it unwraps the DEK
// from server_wrapped_dek and re-seals it for the new password. Without that, a reset would hand
// back an account whose entries can never be decrypted again — so this screen is careful to say
// what actually happened rather than a generic "password updated".

import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../api/auth';
import { ApiError } from '../api/client';
import { Field, Notice, PrimaryButton } from '../components/ui';
import { OreiaGlyph } from '../components/Icons';

export function ResetPasswordScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError('Those two passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Something went wrong. Please try again in a moment.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="oscreen">
      <div className="oscroll">
        <div className="ocolumn opane">
          <div className="orow" style={{ gap: 10, marginBottom: 34 }}>
            <OreiaGlyph style={{ width: 26, height: 26, color: 'var(--ink)' }} />
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 29, lineHeight: 1 }}>Oreia</span>
          </div>

          <h1 className="oserif" style={{ fontSize: 42, margin: '0 0 10px' }}>
            {done ? 'Your journal is open.' : 'Choose a new password.'}
          </h1>

          {!token ? (
            <div className="ostack">
              <Notice tone="danger">
                This link is missing its reset code. Request a new one and open the most recent
                email.
              </Notice>
              <Link className="octa quiet" to="/login">
                Back to sign in
              </Link>
            </div>
          ) : done ? (
            <div className="ostack">
              <p className="otext-soft" style={{ fontSize: 15, lineHeight: 1.6, margin: '0 0 4px', maxWidth: '34ch' }}>
                Your password is changed and your encryption key was re-sealed with it, so every
                entry you have written is readable again.
              </p>
              <PrimaryButton onClick={() => navigate('/login', { replace: true })}>
                Sign in
              </PrimaryButton>
            </div>
          ) : (
            <>
              <p className="otext-soft" style={{ fontSize: 15, lineHeight: 1.6, margin: '0 0 28px', maxWidth: '34ch' }}>
                This also unlocks your journal: your encryption key is re-sealed with the new
                password.
              </p>

              <form className="ostack" style={{ gap: 16 }} onSubmit={onSubmit} noValidate>
                <Field
                  label="New password"
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  hint="At least 8 characters. This also protects your encryption key, so choose something you will not lose."
                />

                <Field
                  label="Confirm password"
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />

                {error ? <Notice tone="danger">{error}</Notice> : null}

                <PrimaryButton type="submit" loading={busy}>
                  Set my new password
                </PrimaryButton>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
